import { LangParam } from '../types';
import { client } from './index';

export interface PremiumFlag {
  status: 'demo' | 'active' | 'expired' | 'cancelled';
  trial_days?: number;
  activation_date: Date;
}
interface FeatureFlagsInterface {
  flags: string[];
  premium_flags?: Record<string, PremiumFlag>;
  premium_features?: Record<string, string>;
}

interface PermissionsInterface {
  permissions: string[];
}

export interface CurrentUser {
  id: number;
  user_id: number;
  username: string;
  email: string;
  last_name: string;
  lang: LangParam;
  help_center_url: string;
  is_support?: boolean;
  is_admin?: boolean;
  is_superadmin?: boolean;
  onboarding_completed?: boolean;
  phone: string;
  company_id: number;
  company_name: string;
  is_paying?: boolean;
  free_form_answers?: number[];
  has_form_answers?: boolean;
  intercom_user_hash?: string;
  country?: string;
  project?: {
    name: string;
    block_editing_creating_forms: boolean;
    forms_allowed_per_account: number;
  };
  auth_type?: string;
}

export interface UserMonthlyActions {
  form_answers_count: number;
  findings_count: number;
  total_actions: number;
}

export interface CreateAccountBody {
  email: string;
  password: string;
  datetime: string;
  captcha_value: string;
  template_id?: string;
}

export interface UserInfoBody {
  first_name: string;
  last_name: string;
  phone_number: string;
  company_name: string;
  industry: string;
}

export interface Onboarding {
  first_name: string;
  last_name: string;
  company_name: string;
  industry: string;
  phone_number: string;
  role: string;
  area: string;
  use_cases: string[];
  potential_users: string;
  email: string;
}

export interface EnableMfaResponse {
  success: boolean;
  secret_code: string;
  qr_code_data: string;
  device_name: string;
  message: string;
}

export interface VerifyMfaResponse {
  success: boolean;
  message: string;
}

export interface DisableMfaResponse {
  success: boolean;
  message: string;
}

export interface MfaStatusResponse {
  mfa_enabled: boolean;
  message?: string;
  user_mfa_setting_list?: string[];
  preferred_mfa_setting?: string;
  mfa_options?: string[];
  error?: string;
}

export interface MfaSettingsStatusResponse {
  settings: 'optional' | 'mandatory' | 'disabled';
  status: 'enabled' | 'disabled';
}

async function getCurrentUserFlags() {
  const response = await client.get<FeatureFlagsInterface>('/users/show_feature_flags');
  const { data } = response;
  return data;
}

async function getCurrentUserPermissions() {
  const response = await client.get<PermissionsInterface>('/show_permission_flags');
  const { data } = response;
  return data.permissions;
}

async function currentUserLogout() {
  const response = await client.get('/react_logout');
  const { data } = response;
  return data;
}

async function getCurrentUserName() {
  const response = await client.get<CurrentUser>('/react_get_username');
  const { data } = response;
  return data;
}

// Add login payload interface after the existing interfaces
interface LoginPayload {
  email: string;
  password?: string;
  recaptcha_token?: string;
  mfa_code?: string;
  mfa_session?: string;
}

const login = async (email: string, password: string, recaptchaToken?: string) => {
  const payload: LoginPayload = {
    email,
    password,
    recaptcha_token: recaptchaToken,
  };

  const response = await client.post('/react_authenticate', payload);
  const { data } = response;
  return data;
};

const verifyMfaLogin = async (email: string, mfaCode: string, mfaSession: string) => {
  const payload: LoginPayload = {
    email,
    mfa_code: mfaCode,
    mfa_session: mfaSession,
  };

  const response = await client.post('/react_authenticate', payload);
  const { data } = response;
  return data;
};

const logout = async () => {
  const response = await client.get('/react_logout');
  const { data } = response;
  return data;
};

const forgotPassword = async (email: string, recaptchaToken: string) => {
  const response = await client.post('/react_forgot_password', {
    email,
    recaptcha_token: recaptchaToken,
  });
  const { data } = response;
  return data;
};

const resetPassword = async (token: string, password: string, password_confirmation: string) => {
  const response = await client.post('/react_reset_password', {
    token,
    password,
    password_confirmation,
  });
  const { data } = response;
  return data;
};

const resetOnboarding = async () => {
  const response = await client.put('/new_onboarding/reset', {});
  const { data } = response;
  return data;
};

const signUp = async (body: CreateAccountBody) => {
  const response = await client.get('/new_onboarding/step_1', { params: body });
  const { data } = response;
  return data;
};

const setUserInfo = async (body: UserInfoBody) => {
  const response = await client.get('/new_onboarding/step_2', { params: body });
  const { data } = response;
  return data;
};

const setRole = async (role: string) => {
  const response = await client.get('/new_onboarding/step_3', { params: { role } });
  const { data } = response;
  return data;
};

const setArea = async (area: string) => {
  const response = await client.get('/new_onboarding/step_4', { params: { area } });
  const { data } = response;
  return data;
};

const setUseCases = async (use_cases: string[]) => {
  const response = await client.get('/new_onboarding/step_5', { params: { use_cases } });
  const { data } = response;
  return data;
};

const setPotentialUsers = async (potential_users: string) => {
  const response = await client.get('/new_onboarding/step_6', { params: { potential_users } });
  const { data } = response;
  return data;
};

const getCurrentOnboarding = async () => {
  const response = await client.get<Onboarding>('/new_onboarding/');
  const { data } = response;
  return data;
};

const getUserMonthlyActions = async () => {
  const response = await client.get<UserMonthlyActions>('/users_monthly_actions');
  const { data } = response;
  return data;
};

export async function enableMfa(deviceName?: string) {
  const response = await client.post<EnableMfaResponse>('/users/2fa/enable', {
    device_name: deviceName || 'Mobile Device',
  });
  const { data } = response;
  return data;
}

export async function verifyMfa(totpCode: string, deviceName?: string) {
  const response = await client.post<VerifyMfaResponse>('/users/2fa/verify', {
    totp_code: totpCode,
    device_name: deviceName || 'Mobile Device',
  });
  const { data } = response;
  return data;
}

export async function disableMfa() {
  const response = await client.delete<DisableMfaResponse>('/users/2fa/disable');
  const { data } = response;
  return data;
}

export async function getMfaStatus() {
  const response = await client.get<MfaStatusResponse>('/users/2fa/status');
  const { data } = response;
  return data;
}

export async function getMfaSettingsStatus() {
  const response = await client.get<MfaSettingsStatusResponse>('users/2fa/settings_status');
  const { data } = response;
  return data;
}

export async function syncCompanyDataToIntercom() {
  client.put('/sync_company_data_to_intercom');
}

export const currentUserApi = {
  getCurrentUserFlags,
  getCurrentUserPermissions,
  currentUserLogout,
  getCurrentUserName,
  login,
  verifyMfaLogin,
  logout,
  forgotPassword,
  resetPassword,
  resetOnboarding,
  signUp,
  setUserInfo,
  setRole,
  setArea,
  setUseCases,
  setPotentialUsers,
  getCurrentOnboarding,
  getUserMonthlyActions,
  enableMfa,
  verifyMfa,
  disableMfa,
  getMfaStatus,
  getMfaSettingsStatus,
  syncCompanyDataToIntercom,
};
